import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { X, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { errMsg } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactModal({ isOpen, onClose, defaultSubject = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: defaultSubject,
    message: ''
  });
  const [sending, setSending] = useState(false);
  // Guard against setState after unmount (modal can be closed mid-request,
  // which previously triggered a React render crash on the next state update).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset subject to default when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, subject: defaultSubject }));
    }
  }, [isOpen, defaultSubject]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('PLEASE FILL IN ALL FIELDS');
      return;
    }

    if (formData.message.length < 10) {
      toast.error('MESSAGE MUST BE AT LEAST 10 CHARACTERS');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      if (!mountedRef.current) return;
      toast.success('MESSAGE SENT! WE\'LL GET BACK TO YOU SOON.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      // Defer the parent's onClose so React can settle the success toast first;
      // closing the modal synchronously while a toast is rendering occasionally
      // raced and triggered an "unmounted setState" crash on production.
      setTimeout(() => {
        try { if (typeof onClose === 'function') onClose(); } catch { /* swallow */ }
      }, 0);
    } catch (error) {
      console.error('Contact form error:', error);
      if (!mountedRef.current) return;
      // errMsg() is crash-proof against FastAPI 422 array detail + circular
      // axios error refs. We still prefer our friendly hardcoded copy where
      // it makes sense.
      const status = error?.response?.status;
      if (status === 503) {
        toast.error('EMAIL SERVICE UNAVAILABLE. PLEASE TRY AGAIN LATER.');
      } else if (status === 429) {
        toast.error('TOO MANY MESSAGES — PLEASE WAIT A MINUTE AND TRY AGAIN.');
      } else {
        toast.error(errMsg(error, 'FAILED TO SEND MESSAGE. PLEASE TRY AGAIN.'));
      }
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="w-full max-w-md bg-zinc-900 border border-zinc-800 p-6 relative animate-in fade-in zoom-in-95 duration-200"
        data-testid="contact-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          data-testid="close-contact-modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="font-sans text-2xl text-white tracking-wider">CONTACT US</h2>
          <p className="font-mono text-xs text-zinc-500 mt-1 uppercase">
            Have a question? We're here to help.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-name" className="font-mono text-xs text-zinc-400">
              NAME
            </Label>
            <Input
              id="contact-name"
              type="text"
              placeholder="YOUR NAME"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-zinc-800/50 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 uppercase"
              data-testid="contact-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-email" className="font-mono text-xs text-zinc-400">
              EMAIL
            </Label>
            <Input
              id="contact-email"
              type="email"
              placeholder="YOUR@EMAIL.COM"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-zinc-800/50 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500"
              data-testid="contact-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-subject" className="font-mono text-xs text-zinc-400">
              SUBJECT
            </Label>
            <Select
              value={formData.subject}
              onValueChange={(value) => setFormData({ ...formData, subject: value })}
            >
              <SelectTrigger 
                className="bg-zinc-800/50 border-zinc-700 text-white font-mono focus:border-tiffany-500 uppercase"
                data-testid="contact-subject-select"
              >
                <SelectValue placeholder="SELECT A TOPIC" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="404 Error" className="font-mono text-sm uppercase">404 ERROR</SelectItem>
                <SelectItem value="Order Issue" className="font-mono text-sm uppercase">ORDER ISSUE</SelectItem>
                <SelectItem value="Product Question" className="font-mono text-sm uppercase">PRODUCT QUESTION</SelectItem>
                <SelectItem value="Shipping Question" className="font-mono text-sm uppercase">SHIPPING QUESTION</SelectItem>
                <SelectItem value="Payment Issue" className="font-mono text-sm uppercase">PAYMENT ISSUE</SelectItem>
                <SelectItem value="Other" className="font-mono text-sm uppercase">OTHER</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-message" className="font-mono text-xs text-zinc-400">
              MESSAGE
            </Label>
            <Textarea
              id="contact-message"
              placeholder="HOW CAN WE HELP?"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="bg-zinc-800/50 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 min-h-[120px] resize-none uppercase"
              data-testid="contact-message-input"
            />
            <p className="font-mono text-[10px] text-zinc-600 text-right">
              {formData.message.length}/2000
            </p>
          </div>

          <Button
            type="submit"
            disabled={sending}
            className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none disabled:opacity-50"
            data-testid="contact-submit-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                SENDING...
              </>
            ) : (
              'SEND MESSAGE'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
